/**
 * <s-load-more> — reveal server-rendered items in batches
 *
 * Markup contract (inside the element):
 *   [data-load-more-item]   — list items; those past the first batch start with `hidden`
 *   [data-load-more-button] — reveals the next batch; removed once nothing is left
 *   [data-load-more-status] — polite live region; announces data-message after each reveal
 *
 * Focus moves to the first link of the newly revealed batch so keyboard users
 * continue from the new products rather than from the button.
 *
 * Attributes:
 *   batch — items revealed per click (default 16)
 */
class SLoadMore extends HTMLElement {
  #ac;

  connectedCallback() {
    const button = this.querySelector('[data-load-more-button]');
    if (!button) return;

    this.#ac = new AbortController();
    button.addEventListener('click', () => this.#reveal(button), { signal: this.#ac.signal });
  }

  disconnectedCallback() {
    this.#ac?.abort();
  }

  get #batch() {
    const value = Number(this.getAttribute('batch'));
    return Number.isInteger(value) && value > 0 ? value : 16;
  }

  #reveal(button) {
    const hiddenItems = [...this.querySelectorAll('[data-load-more-item][hidden]')];
    const next = hiddenItems.slice(0, this.#batch);
    next.forEach((item) => item.removeAttribute('hidden'));

    const status = this.querySelector('[data-load-more-status]');
    if (status instanceof HTMLElement) {
      status.textContent = '';
      requestAnimationFrame(() => { status.textContent = status.dataset.message || ''; });
    }

    const firstLink = next[0]?.querySelector('a[href]');
    if (firstLink instanceof HTMLElement) firstLink.focus({ preventScroll: true });

    if (hiddenItems.length <= next.length) {
      button.closest('div')?.remove();
    }
  }
}

if (!customElements.get('s-load-more')) {
  customElements.define('s-load-more', SLoadMore);
}
